import { useState, useRef, useEffect } from "react";
import { useAtom } from "jotai";
import { logoSrcAtom } from "~/AtomWithDynamoDB";
import logoDefault from "~/assets/images/logo.png";
import { getSignedUrlFromS3, uploadImageToS3, deleteFileFromS3 } from "~/utils/s3Utils";
import { getCustomerId } from "~/components/db/CharacterDBUtils";

/**
 * ロゴ設定に関するhooks
 */
export const useLogoSettings = () => {
  const [logoSrc, setLogoSrc] = useAtom(logoSrcAtom);
  const [displayLogo, setDisplayLogo] = useState<string>(""); // ロゴ画像表示用
  const [isLogoChanged, setIsLogoChanged] = useState<boolean>(false); // 画像変更フラグ
  const fileInputRef = useRef<HTMLInputElement>(null); // ロゴ画像選択用

  useEffect(() => {
    const setLogoUrl = async () => {
      try {
        // AWS環境でかつBase64形式でない場合はS3から署名付きURLを取得して表示
        if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws" && !logoSrc.startsWith("data:")) {
          const s3LogoUrl = await getSignedUrlFromS3(logoSrc);
          if (s3LogoUrl) {
            setDisplayLogo(s3LogoUrl);
          } else {
            setDisplayLogo(logoDefault);
          }
        } else {
          // オンプレ環境またはBase64形式の画像はそのまま表示
          setDisplayLogo(logoSrc);
        }
      } catch (error) {
        console.error("ロゴURLの取得に失敗しました:", error);
        setDisplayLogo(logoDefault);
      }
    };
    setLogoUrl();
  }, [logoSrc]);

  /**
   * ロゴ画像、変更ボタンをクリック時に、画像選択を行う
   */
  const handleLogoClick = () => {
    // ファイル選択前に値をクリアして、同じファイルでも再選択できるようにする
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  /**
   * 画像選択時の処理
   * handleLogoClickでクリックされたinput要素のchangeイベント
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setDisplayLogo(result);
      setIsLogoChanged(true);
    };
    reader.readAsDataURL(file);
  };

  /**
   * ロゴ画像アップロード
   *  - S3アップロード
   *  - DynamoDB更新
   *  - 旧画像削除
   */
  const handleAwsLogoSave = async (
    displayLogo: string,
    logoSrc: string,
    setLogoSrc: (src: string) => void,
    onSuccess: () => void
  ) => {
    const oldLogoPath = logoSrc;
    let newLogoPath = "";
    let uploaded = false;
    let dbUpdated = false;

    try {
      // customerIdを取得
      const customerId = await getCustomerId();

      // S3アップロード
      newLogoPath = await uploadImageToS3(displayLogo, "logos", customerId);
      uploaded = true;

      // DynamoDB更新
      setLogoSrc(newLogoPath);
      dbUpdated = true;

      // デフォルトロゴではない場合のみ旧ロゴを削除
      // パスにlogos/が含まれる場合はS3のロゴと判断
      if (oldLogoPath && oldLogoPath.includes("logos/")) {
        const deleted = await deleteFileFromS3(oldLogoPath);
        if (!deleted) {
          console.error("旧ロゴの削除に失敗しましたが、処理を継続します:", oldLogoPath);
        }
      }

      onSuccess();
    } catch (error) {
      console.error("ロゴ保存処理に失敗しました:", error);

      // ロールバック処理
      if (dbUpdated) {
        setLogoSrc(oldLogoPath);
      }

      if (uploaded && newLogoPath) {
        try {
          await deleteFileFromS3(newLogoPath);
        } catch (rollbackError) {
          console.error("ロールバック中の削除に失敗しました:", rollbackError);
        }
      }

      throw error;
    }
  };

  /**
   * ロゴ画像を保存する処理
   */
  const saveLogo = async (onSuccess: () => void) => {
    if (!displayLogo) return;

    try {
      if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws") {
        await handleAwsLogoSave(displayLogo, logoSrc, setLogoSrc, onSuccess);
      } else {
        // オンプレ環境
        setLogoSrc(displayLogo);
        onSuccess();
      }
      setIsLogoChanged(false);
    } catch (error) {
      console.error("ロゴ保存に失敗しました:", error);
      throw error;
    }
  };

  /**
   * ロゴ画像をデフォルトにリセットする処理
   */
  const resetLogo = () => {
    setDisplayLogo(logoDefault);
    setIsLogoChanged(true);
  };

  /**
   * 未保存の変更があるかどうか
   * 画像が変更され、保存ボタンが押されていない場合のみtrue
   */
  const isNotSaved = isLogoChanged;

  return {
    displayLogo,
    isNotSaved,
    fileInputRef,
    handleLogoClick,
    handleFileChange,
    saveLogo,
    resetLogo,
  };
};
